/**
 * Focus Lock Manager
 * 
 * Provides box-select zoom functionality. User activates focus lock mode,
 * draws a rectangle on the canvas, and the camera zooms to that region.
 */

import * as THREE from 'three';
import type { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import type { IManager } from '../types';

export interface FocusLockManagerConfig {
  /** Highlight color for selection box */
  boxColor?: string;
  /** Box border width in pixels */
  boxBorderWidth?: number;
  /** Animation duration for zoom */
  animationDuration?: number;
  /** Padding factor for zoom (1.0 = exact fit) */
  zoomPadding?: number;
}

export interface FocusLockCallbacks {
  onModeChange?: (active: boolean) => void;
  onZoomComplete?: () => void;
}

export class FocusLockManager implements IManager {
  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private controls: OrbitControls | null = null;
  private renderer: THREE.WebGLRenderer | null = null;
  private raycaster = new THREE.Raycaster();
  
  // Configuration
  private boxColor = 'rgba(0, 123, 255, 0.3)';
  private boxBorderColor = 'rgba(0, 123, 255, 0.8)';
  private boxBorderWidth = 2;
  private animationDuration = 0.5;
  private zoomPadding = 1.3;
  
  // State
  private isActive = false;
  private isDrawing = false;
  private startPoint = { x: 0, y: 0 };
  private endPoint = { x: 0, y: 0 };
  
  // UI Elements
  private selectionBox: HTMLDivElement | null = null;
  
  // Animation
  private isAnimating = false;
  private animationProgress = 0;
  private startPosition = new THREE.Vector3();
  private startTarget = new THREE.Vector3();
  private endPosition = new THREE.Vector3();
  private endTarget = new THREE.Vector3();
  
  // Callbacks
  private callbacks: FocusLockCallbacks = {};
  private requestRender?: () => void;
  
  // Event handlers (stored for cleanup)
  private boundMouseDown: ((e: MouseEvent) => void) | null = null;
  private boundMouseMove: ((e: MouseEvent) => void) | null = null;
  private boundMouseUp: ((e: MouseEvent) => void) | null = null;
  private boundKeyDown: ((e: KeyboardEvent) => void) | null = null;
  
  /**
   * Initialize the manager
   */
  public async init(scene: THREE.Scene, config?: FocusLockManagerConfig): Promise<void> {
    this.scene = scene;
    
    if (config) {
      if (config.boxColor) this.boxColor = config.boxColor;
      if (config.boxBorderWidth) this.boxBorderWidth = config.boxBorderWidth;
      if (config.animationDuration) this.animationDuration = config.animationDuration;
      if (config.zoomPadding) this.zoomPadding = config.zoomPadding;
    }
  }
  
  /**
   * Setup camera, controls, and renderer for interaction
   */
  public setup(
    camera: THREE.PerspectiveCamera,
    controls: OrbitControls,
    renderer: THREE.WebGLRenderer,
    requestRender?: () => void
  ): void {
    this.camera = camera;
    this.controls = controls;
    this.renderer = renderer;
    this.requestRender = requestRender;
    
    this.createSelectionBox();
    this.setupEventListeners();
  }
  
  /**
   * Set callbacks
   */
  public setCallbacks(callbacks: FocusLockCallbacks): void {
    this.callbacks = callbacks;
  }
  
  /**
   * Create the selection box overlay element
   */
  private createSelectionBox(): void {
    if (!this.renderer) return;
    
    const canvas = this.renderer.domElement;
    const parent = canvas.parentElement;
    if (!parent) return;
    
    // Create selection box element
    this.selectionBox = document.createElement('div');
    this.selectionBox.className = 'focus-lock-selection-box';
    this.selectionBox.style.cssText = `
      position: absolute;
      border: ${this.boxBorderWidth}px dashed ${this.boxBorderColor};
      background: ${this.boxColor};
      pointer-events: none;
      display: none;
      z-index: 100;
      box-sizing: border-box;
    `;
    parent.appendChild(this.selectionBox);
  }
  
  /**
   * Setup event listeners for drawing
   */
  private setupEventListeners(): void {
    if (!this.renderer) return;
    
    const canvas = this.renderer.domElement;
    
    this.boundMouseDown = this.handleMouseDown.bind(this);
    this.boundMouseMove = this.handleMouseMove.bind(this);
    this.boundMouseUp = this.handleMouseUp.bind(this);
    this.boundKeyDown = this.handleKeyDown.bind(this);
    
    canvas.addEventListener('mousedown', this.boundMouseDown);
    canvas.addEventListener('mousemove', this.boundMouseMove);
    canvas.addEventListener('mouseup', this.boundMouseUp);
    window.addEventListener('keydown', this.boundKeyDown);
  }
  
  /**
   * Handle mouse down
   */
  private handleMouseDown(event: MouseEvent): void {
    if (!this.isActive || !this.renderer) return;
    
    // Only respond to left click
    if (event.button !== 0) return;
    
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.startPoint = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    this.endPoint = { ...this.startPoint };
    this.isDrawing = true;
    
    // Disable orbit controls while drawing
    if (this.controls) {
      this.controls.enabled = false;
    }
    
    // Show selection box
    if (this.selectionBox) {
      this.selectionBox.style.display = 'block';
      this.updateSelectionBoxPosition();
    }
    
    event.preventDefault();
    event.stopPropagation();
  }
  
  /**
   * Handle mouse move
   */
  private handleMouseMove(event: MouseEvent): void {
    if (!this.isDrawing || !this.renderer) return;
    
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.endPoint = {
      x: Math.max(0, Math.min(event.clientX - rect.left, rect.width)),
      y: Math.max(0, Math.min(event.clientY - rect.top, rect.height)),
    };
    
    this.updateSelectionBoxPosition();
    event.preventDefault();
  }
  
  /**
   * Handle mouse up
   */
  private handleMouseUp(event: MouseEvent): void {
    if (!this.isDrawing) return;
    
    this.isDrawing = false;
    
    // Re-enable orbit controls
    if (this.controls) {
      this.controls.enabled = true;
    }
    
    // Hide selection box
    if (this.selectionBox) {
      this.selectionBox.style.display = 'none';
    }
    
    // Calculate selection size
    const width = Math.abs(this.endPoint.x - this.startPoint.x);
    const height = Math.abs(this.endPoint.y - this.startPoint.y);
    
    // Only zoom if selection is meaningful (> 10px)
    if (width > 10 && height > 10) {
      this.zoomToSelection();
    }
    
    // Deactivate focus lock mode
    this.deactivate();
    
    event.preventDefault();
  }
  
  /**
   * Handle keyboard events
   */
  private handleKeyDown(event: KeyboardEvent): void {
    // Escape cancels focus lock mode
    if (event.key === 'Escape' && this.isActive) {
      this.deactivate();
      event.preventDefault();
    }
  }
  
  /**
   * Update selection box position and size
   */
  private updateSelectionBoxPosition(): void {
    if (!this.selectionBox) return;
    
    const left = Math.min(this.startPoint.x, this.endPoint.x);
    const top = Math.min(this.startPoint.y, this.endPoint.y);
    const width = Math.abs(this.endPoint.x - this.startPoint.x);
    const height = Math.abs(this.endPoint.y - this.startPoint.y);
    
    this.selectionBox.style.left = `${left}px`;
    this.selectionBox.style.top = `${top}px`;
    this.selectionBox.style.width = `${width}px`;
    this.selectionBox.style.height = `${height}px`;
  }
  
  /**
   * Zoom camera to the selected region
   */
  private zoomToSelection(): void {
    if (!this.camera || !this.controls || !this.renderer || !this.scene) return;
    
    const canvas = this.renderer.domElement;
    const rect = canvas.getBoundingClientRect();
    
    // Calculate normalized device coordinates for selection corners
    const left = Math.min(this.startPoint.x, this.endPoint.x);
    const right = Math.max(this.startPoint.x, this.endPoint.x);
    const top = Math.min(this.startPoint.y, this.endPoint.y);
    const bottom = Math.max(this.startPoint.y, this.endPoint.y);
    
    // Center of selection in NDC
    const centerX = ((left + right) / 2 / rect.width) * 2 - 1;
    const centerY = -((top + bottom) / 2 / rect.height) * 2 + 1;
    
    // Cast ray from center of selection
    this.raycaster.setFromCamera(new THREE.Vector2(centerX, centerY), this.camera);
    
    // Find intersections with scene objects
    const intersects = this.raycaster.intersectObjects(this.scene.children, true);
    
    let targetPoint: THREE.Vector3;
    let targetDistance: number;
    
    if (intersects.length > 0) {
      // Use intersection point as target
      targetPoint = intersects[0].point.clone();
      
      // Calculate zoom based on selection size relative to canvas
      const selectionWidth = right - left;
      const selectionHeight = bottom - top;
      const selectionRatio = Math.max(selectionWidth / rect.width, selectionHeight / rect.height);
      
      // Current distance from camera to target
      const currentDistance = this.camera.position.distanceTo(this.controls.target);
      
      // Zoom in proportionally to selection size
      targetDistance = currentDistance * selectionRatio * this.zoomPadding;
      
      // Clamp to controls limits
      targetDistance = Math.max(
        this.controls.minDistance,
        Math.min(this.controls.maxDistance, targetDistance)
      );
    } else {
      // No intersection, just zoom toward center of current view
      targetPoint = this.controls.target.clone();
      const selectionWidth = right - left;
      const selectionHeight = bottom - top;
      const selectionRatio = Math.max(selectionWidth / rect.width, selectionHeight / rect.height);
      const currentDistance = this.camera.position.distanceTo(this.controls.target);
      targetDistance = currentDistance * selectionRatio * this.zoomPadding;
      targetDistance = Math.max(
        this.controls.minDistance,
        Math.min(this.controls.maxDistance, targetDistance)
      );
    }
    
    // Calculate new camera position
    const direction = new THREE.Vector3()
      .subVectors(this.camera.position, this.controls.target)
      .normalize();
    
    const newPosition = new THREE.Vector3()
      .copy(targetPoint)
      .add(direction.multiplyScalar(targetDistance));
    
    // Animate to new view
    this.animateToView(
      newPosition.toArray() as [number, number, number],
      targetPoint.toArray() as [number, number, number]
    );
  }
  
  /**
   * Animate camera to new position and target
   */
  private animateToView(
    position: [number, number, number],
    target: [number, number, number]
  ): void {
    if (!this.camera || !this.controls) return;
    
    // Store current state
    this.startPosition.copy(this.camera.position);
    this.startTarget.copy(this.controls.target);
    
    // Set target state
    this.endPosition.set(...position);
    this.endTarget.set(...target);
    
    // Start animation
    this.isAnimating = true;
    this.animationProgress = 0;
  }
  
  /**
   * Easing function (ease-in-out cubic)
   */
  private easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }
  
  /**
   * Update animation (called every frame)
   */
  public update(deltaTime: number): void {
    if (!this.isAnimating || !this.camera || !this.controls) return;
    
    this.animationProgress += deltaTime / this.animationDuration;
    
    if (this.animationProgress >= 1) {
      // Animation complete
      this.camera.position.copy(this.endPosition);
      this.controls.target.copy(this.endTarget);
      this.isAnimating = false;
      this.callbacks.onZoomComplete?.();
    } else {
      // Interpolate
      const t = this.easeInOutCubic(this.animationProgress);
      
      this.camera.position.lerpVectors(this.startPosition, this.endPosition, t);
      this.controls.target.lerpVectors(this.startTarget, this.endTarget, t);
    }
    
    this.controls.update();
    this.requestRender?.();
  }
  
  /**
   * Activate focus lock mode
   */
  public activate(): void {
    if (this.isActive) return;
    
    this.isActive = true;
    
    // Change cursor to crosshair
    if (this.renderer) {
      this.renderer.domElement.style.cursor = 'crosshair';
    }
    
    this.callbacks.onModeChange?.(true);
  }
  
  /**
   * Deactivate focus lock mode
   */
  public deactivate(): void {
    if (!this.isActive) return;
    
    this.isActive = false;
    this.isDrawing = false;
    
    // Restore cursor
    if (this.renderer) {
      this.renderer.domElement.style.cursor = 'grab';
    }
    
    // Hide selection box
    if (this.selectionBox) {
      this.selectionBox.style.display = 'none';
    }
    
    // Re-enable controls
    if (this.controls) {
      this.controls.enabled = true;
    }
    
    this.callbacks.onModeChange?.(false);
  }
  
  /**
   * Toggle focus lock mode
   */
  public toggle(): void {
    if (this.isActive) {
      this.deactivate();
    } else {
      this.activate();
    }
  }
  
  /**
   * Check if focus lock mode is active
   */
  public isActiveMode(): boolean {
    return this.isActive;
  }
  
  /**
   * Check if currently animating
   */
  public isTransitioning(): boolean {
    return this.isAnimating;
  }
  
  /**
   * Dispose
   */
  public dispose(): void {
    // Remove event listeners
    if (this.renderer && this.boundMouseDown) {
      const canvas = this.renderer.domElement;
      canvas.removeEventListener('mousedown', this.boundMouseDown);
      canvas.removeEventListener('mousemove', this.boundMouseMove!);
      canvas.removeEventListener('mouseup', this.boundMouseUp!);
    }
    
    if (this.boundKeyDown) {
      window.removeEventListener('keydown', this.boundKeyDown);
    }
    
    // Remove selection box
    if (this.selectionBox && this.selectionBox.parentElement) {
      this.selectionBox.parentElement.removeChild(this.selectionBox);
    }
    
    this.scene = null;
    this.camera = null;
    this.controls = null;
    this.renderer = null;
    this.selectionBox = null;
  }
}
